import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const USER_WIKI_SERVER_NAME = "user_wiki" as const;
export const USER_WIKI_READ_TOOL_NAME = "read_page";
export const USER_WIKI_WRITE_TOOL_NAME = "write_section";
export const USER_WIKI_CREATE_PERSON_TOOL_NAME = "create_person_page";
export const USER_WIKI_CREATE_PAGE_TOOL_NAME = "create_page";
export const USER_WIKI_REVISE_CLAIM_TOOL_NAME = "revise_claim";
export const USER_WIKI_SET_FACTS_TOOL_NAME = "set_page_facts";
export const USER_WIKI_MAINTAIN_TOOL_NAME = "run_maintenance";
export const USER_WIKI_SEARCH_CLAIMS_TOOL_NAME = "search_claims";
export const USER_WIKI_GET_CLAIM_EVIDENCE_TOOL_NAME = "get_claim_evidence";
export const USER_WIKI_LIST_CLAIM_CONFLICTS_TOOL_NAME = "list_claim_conflicts";

const sourcesSchema = z
  .array(
    z.object({
      type: z
        .string()
        .describe(
          "What kind of evidence this is: conversation, task, message, url."
        ),
      id: z
        .string()
        .describe(
          "Identifier the citation resolves through: a conversation sId, a task sId, or the URL itself."
        ),
      title: z.string().nullable().describe("Human-readable label, or null."),
      url: z.string().nullable().describe("Link to the evidence, or null."),
    })
  )
  .describe(
    "The evidence behind this edit. At least one is required — an edit no source backs does not belong in the wiki."
  );

const claimSourcesSchema = z
  .array(
    z.object({
      type: z
        .enum(["email", "calendar", "doc", "conversation", "web", "task"])
        .describe("What kind of evidence this is."),
      externalId: z
        .string()
        .describe(
          "Identifier in that system: a Gmail message id, an event id, a conversation sId."
        ),
      title: z
        .string()
        .nullable()
        .describe("Human-readable label — a thread subject, an event title."),
      deepLink: z
        .string()
        .nullable()
        .describe(
          "URL that opens the evidence itself. A citation nobody can click is decoration."
        ),
      observedAt: z
        .string()
        .nullable()
        .describe("ISO date the evidence is from, or null."),
    })
  )
  .describe("Evidence for this claim. At least one, each with a deep link.");

const claimsSchema = z
  .array(
    z.object({
      text: z
        .string()
        .describe("One statement. Split anything with an 'and' in it."),
      confidence: z
        .enum(["high", "medium", "low"])
        .describe(
          "high: stated outright by the person. medium: consistent across several observations. low: one weak signal."
        ),
      observedAt: z
        .string()
        .nullable()
        .describe("ISO date the claim was observed, or null for now."),
      sources: claimSourcesSchema,
    })
  )
  .describe(
    "The section as addressable claims, each footnoted separately. Prefer this over `body`: a claim is what a later contradiction can revise."
  );

const factsSchema = z
  .array(
    z.object({
      label: z.string().describe("Row label, e.g. `Tone`."),
      value: z.string().describe("Row value, e.g. `Blunt, no pleasantries`."),
      linkSlug: z
        .string()
        .nullable()
        .describe("Wiki page this value points at, or null."),
    })
  )
  .describe("Ordered infobox rows, 3-6 of them. Short values, no prose.");

export const USER_WIKI_TOOLS_METADATA = [
  {
    name: USER_WIKI_READ_TOOL_NAME,
    description:
      "Read a page of the current user's personal wiki, with its sections. Omit `slug` to list the page tree first.",
    schema: {
      slug: z
        .string()
        .nullable()
        .describe(
          "Page to read, e.g. `profile`, `goals`, or a person page's slug. Null lists every page instead."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Reading wiki",
      done: "Read wiki",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_WRITE_TOOL_NAME,
    description:
      "Rewrite one section of a page in the current user's personal wiki. Every write is recorded in the wiki changelog with its summary and sources, so state what changed and cite the evidence. Mark anything you inferred as an inference, and say so in the text when the evidence is thin.",
    schema: {
      slug: z.string().describe("Page to write to."),
      anchor: z
        .string()
        .describe(
          "Section anchor from `read_page`, e.g. `communication-style`."
        ),
      body: z
        .string()
        .describe(
          'The section\'s new markdown body, replacing what is there. Write claims you can support; hedge the rest explicitly ("only one sent email is available, so this is not yet observable"). Link other pages as [[slug]].'
        ),
      claims: claimsSchema
        .nullable()
        .describe(
          "The section as claims. When given, these replace the section and `body` is ignored."
        ),
      summary: z
        .string()
        .describe(
          "One changelog line: what changed and on what evidence, in the past tense."
        ),
      sources: sourcesSchema,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Writing wiki",
      done: "Wrote wiki",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_CREATE_PERSON_TOOL_NAME,
    description:
      "Create a person page in the current user's personal wiki for someone they work with. Record what the interaction actually shows, including what remains uncertain about the relationship.",
    schema: {
      name: z.string().describe("The person's display name."),
      email: z
        .string()
        .nullable()
        .describe(
          "The address this person writes from, or null if no message from them is on record. Without it the page can never be re-researched."
        ),
      slug: z
        .string()
        .describe("Stable lowercase-hyphenated slug, e.g. `sourav-das`."),
      body: z
        .string()
        .describe(
          "Markdown for the page's first section: what this person does, how they work with the user, and what is still unknown."
        ),
      summary: z
        .string()
        .describe("One changelog line describing why this page now exists."),
      sources: sourcesSchema,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Creating person page",
      done: "Created person page",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_CREATE_PAGE_TOOL_NAME,
    description:
      "Create a page in the current user's personal wiki. Use `memory-<theme>` to group a theme that recurs across sources, or `profile-<aspect>` to give one aspect of the profile its own page.",
    schema: {
      kind: z
        .enum(["memory", "profile", "notes", "projects", "goals"])
        .describe(
          "Tree slot. `memory` files the page under Notes; `profile` hangs it under Profile when the slug is prefixed `profile-`."
        ),
      slug: z
        .string()
        .describe(
          "Lowercase-hyphenated, prefixed by its parent: `memory-ai-product-work`, `profile-communication-style`."
        ),
      title: z.string().describe("Page title as the user will read it."),
      description: z
        .string()
        .nullable()
        .describe("One line under the title, or null."),
      heading: z
        .string()
        .describe("Heading of the page's first section, e.g. `What we know`."),
      claims: claimsSchema,
      facts: factsSchema
        .nullable()
        .describe("Infobox rows for the page, or null for no card."),
      summary: z
        .string()
        .describe("One changelog line explaining why this page now exists."),
      sources: sourcesSchema,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Creating wiki page",
      done: "Created wiki page",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_REVISE_CLAIM_TOOL_NAME,
    description:
      "Revise one claim of the wiki when new evidence contradicts it. State the reason and cite what displaced it — the old claim stops rendering but stays in the changelog.",
    schema: {
      claimId: z
        .string()
        .describe("Claim id from `read_page`, e.g. `wkc_...`."),
      newText: z
        .string()
        .nullable()
        .describe(
          "Corrected statement, or null to retract without replacing it."
        ),
      newStatus: z
        .enum(["revised", "retracted"])
        .describe(
          "`revised` when newText replaces it, `retracted` when the claim simply no longer holds."
        ),
      confidence: z
        .enum(["high", "medium", "low"])
        .nullable()
        .describe("Confidence of the replacement claim, or null for medium."),
      reason: z
        .string()
        .describe(
          "The changelog line: what contradicted it, and what is believed now."
        ),
      sources: claimSourcesSchema.describe(
        "The evidence that displaced the old claim."
      ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Revising wiki claim",
      done: "Revised wiki claim",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_MAINTAIN_TOOL_NAME,
    description:
      "Run the maintenance pass over the current user's personal wiki: diff the signals that arrived since the last run, transition the goals they resolve, revise the claims they contradict, and stamp a quiet day as quiet. Takes no arguments — what it does is decided by the evidence, not by the caller. Call it once per run and report the line it returns.",
    schema: {},
    stake: "never_ask",
    displayLabels: {
      running: "Updating wiki",
      done: "Updated wiki",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_SET_FACTS_TOOL_NAME,
    description:
      "Set the infobox card on a wiki page: a few short key/value rows summarizing what the page establishes.",
    schema: {
      slug: z.string().describe("Page to set the card on."),
      facts: factsSchema,
      summary: z
        .string()
        .describe("One changelog line describing what the card now states."),
      sources: sourcesSchema,
    },
    stake: "never_ask",
    displayLabels: {
      running: "Writing wiki infobox",
      done: "Wrote wiki infobox",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_SEARCH_CLAIMS_TOOL_NAME,
    description:
      "Search the current user's current personal claims for what is relevant to a question or the people in the room. Returns claim text, compact citations, and any unresolved conflicts. Sensitive and proposed claims are excluded. Relevant claims also arrive automatically before you answer; use this when you need a targeted search beyond that block.",
    schema: {
      query: z
        .string()
        .describe("The question or message to retrieve claims for."),
      participantEntityKeys: z
        .array(z.string())
        .nullable()
        .describe(
          "Entity keys for people in the room — emails, names — so a claim about them is kept even if the query never names them. Null means none."
        ),
      topK: z
        .number()
        .nullable()
        .describe("Maximum claims to return. Null uses 10."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching claims",
      done: "Searched claims",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_GET_CLAIM_EVIDENCE_TOOL_NAME,
    description:
      "Fetch the stored excerpts and deep links that a personal claim cites. Use this when a claim is disputed, a citation must be shown, or you need primary evidence. Returns excerpts and links only — never the original private payload.",
    schema: {
      claimId: z
        .string()
        .describe("Claim id from search_claims or read_page, e.g. `wkc_...`."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Reading claim evidence",
      done: "Read claim evidence",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: USER_WIKI_LIST_CLAIM_CONFLICTS_TOOL_NAME,
    description:
      "List current personal claims that contradict the given claim on the same subject and predicate. Conflicts are returned unresolved — do not pick a winner. Use this when two retrieved claims disagree or the user disputes one.",
    schema: {
      claimId: z
        .string()
        .describe("Claim id from search_claims or read_page, e.g. `wkc_...`."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing claim conflicts",
      done: "Listed claim conflicts",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const USER_WIKI_SERVER = {
  serverInfo: {
    name: USER_WIKI_SERVER_NAME,
    version: "1.0.0",
    description:
      "Read, search, and maintain the current user's personal wiki: overview, goals, projects, profile, notes, theme and person pages, every statement an addressable claim with its own dated evidence, and every edit landing in a dated changelog.",
    authorization: null,
    icon: "ActionDocumentTextIcon",
    documentationUrl: null,
  },
  tools: USER_WIKI_TOOLS_METADATA,
} as const satisfies ServerMetadata;
